// @ts-check
// Lista das campanhas e comunicados de WhatsApp (/campaigns/whatsapp; spec/whatsapp.md 18.1; contrato 09 de campanhas de
// WhatsApp, seção 14): "Nova campanha" com Marketing e Comunicado, o limite diário da Meta por número, chips de situação
// e busca na URL, tabela (cartões no celular), menu de cada linha e atualização a cada 15 s enquanto alguma envia. O
// atalho "Usar numa campanha" do modelo chega com `?new=1&kind=&templateId=` e vira um rascunho aberto no editor.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { listChannels } from '../../data/conversations.js';
import { getWhatsappMessagingLimit, listWhatsappTemplates } from '../../data/integrations.js';
import {
  cancelWhatsappCampaign, createWhatsappCampaign, deleteWhatsappCampaign, duplicateWhatsappCampaign, listWhatsappCampaigns, pauseWhatsappCampaign,
  resumeWhatsappCampaign,
} from '../../data/whatsapp-campaigns.js';
import { ApiError } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { MenuKeyboardScope } from '../../ui/menu-keyboard.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { channelLabel } from '../../lib/whatsapp-templates.js';
import {
  CAMPAIGN_STATUS_FILTERS, campaignStatusTone, canCancelCampaign, canDeleteCampaign, canPauseCampaign, listRefreshInterval, percentOf,
  readNewCampaignRequest, readStatusFilter, statusQueryForFilter,
} from '../../lib/whatsapp-campaigns.js';
import { usePolling } from '../conversations/conversations-state.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaignSummary} WhatsappCampaignSummary
 * @typedef {import('../../data/conversations.js').Channel} Channel
 */

/** Texto de um motivo de fora do envio; motivo sem texto próprio vira "Outro motivo". @param {string} reason */
export const deliveryReasonText = (reason) => (hasTranslation(`deliveryReasons.${reason}`) ? t(`deliveryReasons.${reason}`) : t('whatsappCampaigns.skipReasonOther'));

/** @param {string} campaignId */
export const whatsappCampaignHref = (campaignId) => `/campaigns/whatsapp/${encodeURIComponent(campaignId)}`;

/** @param {MouseEvent} event */
const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** Selo da situação, com o motivo curto da espera. @param {{ campaign: WhatsappCampaignSummary }} props */
export function CampaignStatusBadge({ campaign }) {
  const waiting = campaign.status === 'SENDING' && campaign.waitingReason ? t(`whatsappCampaigns.waitingShort.${campaign.waitingReason}`) : null;
  return html`<span class="wa-badge wa-campaign-status" data-tone=${campaignStatusTone(campaign.status)} data-status=${campaign.status}>
    ${t(`whatsappCampaigns.status.${campaign.status}`)}${waiting ? html`<span class="wa-campaign-status-detail"> · ${waiting}</span>` : null}
  </span>`;
}

/** Selo do tipo. @param {{ kind: string }} props */
export function CampaignKindBadge({ kind }) {
  return html`<span class="wa-badge" data-tone=${kind === 'MARKETING' ? 'purple' : 'blue'}>${t(`whatsappCampaigns.kind.${kind}`)}</span>`;
}

/**
 * Faixa do limite diário da Meta de um número.
 * @param {{ channel: Channel, showNumber: boolean }} props
 */
function LimitBanner({ channel, showNumber }) {
  const [data, setData] = useState(/** @type {any} */ (null));
  useEffect(() => {
    getWhatsappMessagingLimit(channel.id).then(setData).catch(() => setData(null));
  }, [channel.id]);
  if (!data?.portfolio) return null;
  const { portfolio } = data;
  const text = portfolio.limit === null
    ? t('whatsappCampaigns.limits.unlimited', { used: formatNumber(portfolio.usedLast24h) })
    : t('whatsappCampaigns.limits.banner', { limit: formatNumber(portfolio.limit), used: formatNumber(portfolio.usedLast24h), cap: formatNumber(portfolio.cap ?? 0) });
  return html`<div class="wa-campaign-limit" data-testid="whatsapp-campaign-limit">
    <${Icon} name="IconProgress" size=${16} />
    <span>${showNumber ? html`<strong>${channelLabel(channel)}: </strong>` : null}${text}</span>
    <details class="wa-campaign-limit-help">
      <summary>${t('whatsappCampaigns.limits.understand')}</summary>
      <p>${t('whatsappCampaigns.limits.help')}</p>
      ${!portfolio.isLimitConfirmed ? html`<p>${t('whatsappCampaigns.limits.notConfirmed')}</p>` : null}
    </details>
  </div>`;
}

/**
 * Menu "Nova campanha": Marketing ou Comunicado.
 * @param {{ onCreate: (kind: 'MARKETING' | 'ANNOUNCEMENT') => void, isCreating: boolean, disabled: boolean }} props
 */
function NewCampaignMenu({ onCreate, isCreating, disabled }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  /** @param {'MARKETING' | 'ANNOUNCEMENT'} kind */
  const choose = (kind) => {
    setIsOpen(false);
    onCreate(kind);
  };
  return html`<span ref=${anchorRef} class="wa-campaign-new">
    <${Button} title=${t('whatsappCampaigns.newCampaign')} icon="IconPlus" accent="blue" size="small" isLoading=${isCreating} disabled=${disabled}
      aria-haspopup="menu" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => setIsOpen(!isOpen)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${300} placement="bottom-end" role="menu">
      <${DropdownMenuItems}>
        <${MenuKeyboardScope} isOpen=${isOpen} anchorRef=${anchorRef} onClose=${() => setIsOpen(false)}>
          <${MenuItem} icon="IconSpeakerphone" text=${t('whatsappCampaigns.newMarketing')} contextualText=${t('whatsappCampaigns.newMarketingHint')}
            className="wa-campaign-new-option" onClick=${() => choose('MARKETING')} />
          <${MenuItem} icon="IconBell" text=${t('whatsappCampaigns.newAnnouncement')} contextualText=${t('whatsappCampaigns.newAnnouncementHint')}
            className="wa-campaign-new-option" onClick=${() => choose('ANNOUNCEMENT')} />
        <//>
      <//>
    <//>
  </span>`;
}

/**
 * Menu de três pontos de uma campanha.
 * @param {{ campaign: WhatsappCampaignSummary, onAction: (action: string, campaign: WhatsappCampaignSummary) => void }} props
 */
function CampaignRowMenu({ campaign, onAction }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  /** @param {string} action */
  const run = (action) => {
    setIsOpen(false);
    onAction(action, campaign);
  };
  return html`<span ref=${anchorRef} class="wa-campaign-row-menu" onClick=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}>
    <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('whatsappCampaigns.actions.menu', { name: campaign.name })} onClick=${() => setIsOpen(!isOpen)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${220} placement="bottom-end" role="menu">
      <${DropdownMenuItems}>
        <${MenuKeyboardScope} isOpen=${isOpen} anchorRef=${anchorRef} onClose=${() => setIsOpen(false)}>
        <${MenuItem} icon="IconExternalLink" text=${t('whatsappCampaigns.actions.open')} onClick=${() => run('open')} />
        <${MenuItem} icon="IconCopy" text=${t('whatsappCampaigns.actions.duplicate')} onClick=${() => run('duplicate')} />
        ${canPauseCampaign(campaign.status) ? html`<${MenuItem} icon="IconPlayerPause" text=${t('whatsappCampaigns.actions.pause')} onClick=${() => run('pause')} />` : null}
        ${campaign.status === 'PAUSED' ? html`<${MenuItem} icon="IconPlayerPlay" text=${t('whatsappCampaigns.actions.resume')} onClick=${() => run('resume')} />` : null}
        ${canCancelCampaign(campaign.status) ? html`<${MenuItem} icon="IconCircleX" text=${t('whatsappCampaigns.actions.cancel')} onClick=${() => run('cancel')} />` : null}
        ${canDeleteCampaign(campaign.status) ? html`<${MenuItem} icon="IconTrash" accent="danger" text=${t('whatsappCampaigns.actions.delete')} onClick=${() => run('delete')} />` : null}
        <//>
      <//>
    <//>
  </span>`;
}

/** Entregues e lidas com o percentual. @param {{ value: number, total: number }} props */
function CountWithRate({ value, total }) {
  const rate = percentOf(value, total);
  return html`<span class="wa-campaign-count">${formatNumber(value)}${rate !== null ? html`<span class="wa-campaign-rate"> (${rate}%)</span>` : null}</span>`;
}

export function WhatsappCampaignsPage() {
  const isMobile = useIsMobile();
  const isAdmin = hasCurrentPermissionFlag('WORKSPACE');
  const statusFilter = readStatusFilter(searchParamsState.value.get('status'));
  const urlSearch = searchParamsState.value.get('search') ?? '';
  const [searchText, setSearchText] = useState(urlSearch);
  const [campaigns, setCampaigns] = useState(/** @type {WhatsappCampaignSummary[] | null} */ (null));
  const [endCursor, setEndCursor] = useState(/** @type {string | null} */ (null));
  const [loadFailed, setLoadFailed] = useState(false);
  const [channels, setChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(/** @type {{ action: 'cancel' | 'delete', campaign: WhatsappCampaignSummary } | null} */ (null));
  const [isConfirming, setIsConfirming] = useState(false);
  const newRequest = readNewCampaignRequest(searchParamsState.value);
  const handledNewRequest = useRef(false);

  const load = (/** @type {{ silent?: boolean }} */ options = {}) => {
    if (!options.silent) setLoadFailed(false);
    return listWhatsappCampaigns({ status: statusQueryForFilter(statusFilter) || undefined, search: urlSearch || undefined, limit: 50 })
      .then((page) => {
        setCampaigns(page.campaigns);
        setEndCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      })
      .catch((error) => {
        if (options.silent) return;
        setLoadFailed(true);
        showErrorSnackbar(error);
      });
  };
  useEffect(() => {
    setCampaigns(null);
    load();
  }, [statusFilter, urlSearch]);
  useEffect(() => {
    listChannels()
      .then((data) => setChannels(data.channels.filter((channel) => channel.type === 'WHATSAPP' && channel.isActive !== false)))
      .catch(() => setChannels([]));
  }, []);
  // busca na URL depois de uma pausa na digitação
  useEffect(() => {
    if (searchText === urlSearch) return undefined;
    const timer = setTimeout(() => updateSearchParams({ search: searchText.trim() || null }), 400);
    return () => clearTimeout(timer);
  }, [searchText]);
  const refreshInterval = listRefreshInterval(campaigns);
  usePolling(() => load({ silent: true }), refreshInterval ?? 15_000, refreshInterval !== null);

  /**
   * Cria o rascunho e abre o editor (com o modelo do atalho, quando veio da página do modelo).
   * @param {'MARKETING' | 'ANNOUNCEMENT'} kind @param {string | null} [templateId]
   */
  const createDraft = async (kind, templateId = null) => {
    setIsCreating(true);
    try {
      /** @type {Record<string, unknown>} */
      const input = { kind };
      if (templateId) {
        const template = (await listWhatsappTemplates({})).find((candidate) => candidate.id === templateId);
        if (template) Object.assign(input, { templateId: template.id, channelId: template.channelId });
      } else if (channels && channels.length === 1) input.channelId = channels[0].id;
      const campaign = await createWhatsappCampaign(input);
      navigate(whatsappCampaignHref(campaign.id), { replace: Boolean(templateId) });
    } catch (error) {
      showErrorSnackbar(error);
      if (templateId) updateSearchParams({ new: null, kind: null, templateId: null });
    } finally {
      setIsCreating(false);
    }
  };
  useEffect(() => {
    if (!newRequest || handledNewRequest.current || channels === null) return;
    handledNewRequest.current = true;
    createDraft(newRequest.kind, newRequest.templateId);
  }, [channels, newRequest?.kind, newRequest?.templateId]);

  /** @param {string} action @param {WhatsappCampaignSummary} campaign */
  const onAction = async (action, campaign) => {
    try {
      if (action === 'open') navigate(whatsappCampaignHref(campaign.id));
      else if (action === 'duplicate') {
        const copy = await duplicateWhatsappCampaign(campaign.id);
        navigate(whatsappCampaignHref(copy.id));
      } else if (action === 'pause') {
        await pauseWhatsappCampaign(campaign.id);
        showSuccessSnackbar(t('whatsappCampaigns.actions.paused'));
        await load();
      } else if (action === 'resume') {
        await resumeWhatsappCampaign(campaign.id);
        showSuccessSnackbar(t('whatsappCampaigns.actions.resumed'));
        await load();
      } else if (action === 'cancel' || action === 'delete') setPendingConfirm({ action, campaign });
    } catch (error) {
      // a retomada que pede confirmação (qualidade baixa, muitas saídas) é feita no relatório
      if (error instanceof ApiError && error.code === 'ACKNOWLEDGEMENT_REQUIRED') navigate(`${whatsappCampaignHref(campaign.id)}?resume=1`);
      else showErrorSnackbar(error);
    }
  };
  const confirm = async () => {
    if (!pendingConfirm) return;
    setIsConfirming(true);
    try {
      if (pendingConfirm.action === 'cancel') await cancelWhatsappCampaign(pendingConfirm.campaign.id);
      else await deleteWhatsappCampaign(pendingConfirm.campaign.id);
      showSuccessSnackbar(t(pendingConfirm.action === 'cancel' ? 'whatsappCampaigns.actions.canceled' : 'whatsappCampaigns.actions.deleted'));
      setPendingConfirm(null);
      await load();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsConfirming(false);
    }
  };
  const loadMore = () => {
    if (!endCursor) return;
    listWhatsappCampaigns({ status: statusQueryForFilter(statusFilter) || undefined, search: urlSearch || undefined, limit: 50, startingAfter: endCursor })
      .then((page) => {
        setCampaigns([...(campaigns ?? []), ...page.campaigns]);
        setEndCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      })
      .catch(showErrorSnackbar);
  };

  const hasChannels = (channels ?? []).length > 0;
  const channelsWithWaba = (channels ?? []).filter((channel) => Boolean(channel.settings?.wabaId));
  /** @param {WhatsappCampaignSummary} campaign */
  const openRow = (campaign) => (/** @type {MouseEvent} */ event) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    navigate(whatsappCampaignHref(campaign.id));
  };

  const renderRows = () => {
    if (isMobile) {
      return html`<ul class="wa-campaign-cards" aria-label=${t('whatsappCampaigns.title')}>
        ${(campaigns ?? []).map((campaign) => html`<li key=${campaign.id} class="wa-campaign-card">
          <a class="wa-campaign-card-link" href=${whatsappCampaignHref(campaign.id)} onClick=${openRow(campaign)}>
            <span class="wa-campaign-name">${campaign.name}</span>
            <span class="wa-campaign-card-badges"><${CampaignKindBadge} kind=${campaign.kind} /><${CampaignStatusBadge} campaign=${campaign} /></span>
            <span class="wa-campaign-card-metrics">
              ${t('whatsappCampaigns.list.deliveredShort', { count: formatNumber(campaign.counts.delivered), rate: percentOf(campaign.counts.delivered, campaign.counts.accepted) ?? 0 })}
              · ${t('whatsappCampaigns.list.readShort', { rate: percentOf(campaign.counts.read, campaign.counts.delivered) ?? 0 })}
            </span>
          </a>
          <${CampaignRowMenu} campaign=${campaign} onAction=${onAction} />
        </li>`)}
      </ul>`;
    }
    return html`<div class="wa-campaign-table-wrapper">
      <table class="wa-campaign-table">
        <thead><tr>
          <th scope="col">${t('whatsappCampaigns.list.columns.name')}</th>
          <th scope="col">${t('whatsappCampaigns.list.columns.kind')}</th>
          <th scope="col">${t('whatsappCampaigns.list.columns.status')}</th>
          <th scope="col">${t('whatsappCampaigns.list.columns.number')}</th>
          <th scope="col">${t('whatsappCampaigns.list.columns.template')}</th>
          <th scope="col" class="wa-number-cell">${t('whatsappCampaigns.list.columns.recipients')}</th>
          <th scope="col" class="wa-number-cell">${t('whatsappCampaigns.list.columns.delivered')}</th>
          <th scope="col" class="wa-number-cell">${t('whatsappCampaigns.list.columns.read')}</th>
          <th scope="col" class="wa-number-cell">${t('whatsappCampaigns.list.columns.replies')}</th>
          <th scope="col">${t('whatsappCampaigns.list.columns.updatedAt')}</th>
          <th scope="col"><span class="wa-visually-hidden">${t('whatsappCampaigns.actions.menuColumn')}</span></th>
        </tr></thead>
        <tbody>
          ${(campaigns ?? []).map((campaign) => html`<tr key=${campaign.id} class="wa-campaign-row" onClick=${openRow(campaign)}>
            <td><a class="wa-campaign-name" href=${whatsappCampaignHref(campaign.id)} onClick=${openRow(campaign)}>${campaign.name}</a></td>
            <td><${CampaignKindBadge} kind=${campaign.kind} /></td>
            <td><${CampaignStatusBadge} campaign=${campaign} /></td>
            <td>${campaign.channel?.label ?? ''}</td>
            <td>${campaign.template?.name ?? ''}</td>
            <td class="wa-number-cell">${formatNumber(campaign.counts.recipients)}</td>
            <td class="wa-number-cell"><${CountWithRate} value=${campaign.counts.delivered} total=${campaign.counts.accepted} /></td>
            <td class="wa-number-cell">${percentOf(campaign.counts.read, campaign.counts.delivered) ?? 0}%</td>
            <td class="wa-number-cell">${formatNumber(campaign.counts.replied)}</td>
            <td>${formatDateTime(campaign.updatedAt)}</td>
            <td class="wa-campaign-menu-cell"><${CampaignRowMenu} campaign=${campaign} onAction=${onAction} /></td>
          </tr>`)}
        </tbody>
      </table>
    </div>`;
  };

  const renderBody = () => {
    if (loadFailed) {
      return html`<div class="wa-campaign-state" role="alert">
        <p>${t('whatsappCampaigns.list.loadFailed')}</p>
        <${Button} title=${t('whatsappCampaigns.list.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${() => load()} />
      </div>`;
    }
    if (campaigns === null || channels === null || newRequest) {
      return html`<div class="wa-campaign-skeleton" aria-busy="true">${[0, 1, 2, 3, 4].map((index) => html`<${Skeleton} key=${index} height=${40} radius=${6} />`)}</div>`;
    }
    if (!hasChannels && campaigns.length === 0) {
      return html`<${EmptyState} icon="IconBrandWhatsapp" title=${t('whatsappCampaigns.noChannel.title')}
        subtitle=${isAdmin ? t('whatsappCampaigns.noChannel.admin') : t('whatsappCampaigns.noChannel.member')}
        action=${isAdmin ? html`<${Button} title=${t('whatsappCampaigns.noChannel.goToChannels')} icon="IconSettings" variant="secondary" size="small" onClick=${() => navigate('/settings/channels')} />` : null} />`;
    }
    if (campaigns.length === 0) {
      return statusFilter !== 'ALL' || urlSearch
        ? html`<p class="wa-campaign-state">${t('whatsappCampaigns.list.noResults')}</p>`
        : html`<${EmptyState} icon="IconBrandWhatsapp" title=${t('whatsappCampaigns.empty.title')} subtitle=${t('whatsappCampaigns.empty.description')}
            action=${html`<${NewCampaignMenu} onCreate=${(kind) => createDraft(kind)} isCreating=${isCreating} disabled=${!hasChannels} />`} />`;
    }
    return html`${renderRows()}
      ${endCursor ? html`<div class="wa-campaign-more"><${Button} title=${t('whatsappCampaigns.list.loadMore')} variant="secondary" size="small" onClick=${loadMore} /></div>` : null}`;
  };

  return html`<${PageCard}
    className="campaigns-screen wa-campaigns-page"
    header=${html`<${PageHeader} icon="IconBrandWhatsapp" title=${t('whatsappCampaigns.title')}
      actions=${hasChannels ? html`<${NewCampaignMenu} onCreate=${(kind) => createDraft(kind)} isCreating=${isCreating} disabled=${false} />` : null} />`}
  >
    <div class="wa-campaigns-content">
      ${channelsWithWaba.map((channel) => html`<${LimitBanner} key=${channel.id} channel=${channel} showNumber=${channelsWithWaba.length > 1} />`)}
      <div class="wa-campaign-toolbar">
        <div class="wa-campaign-chips" role="group" aria-label=${t('whatsappCampaigns.list.filterLabel')}>
          ${CAMPAIGN_STATUS_FILTERS.map((filter) => html`<button key=${filter.key} type="button" class="wa-campaign-chip"
            aria-pressed=${filter.key === statusFilter ? 'true' : 'false'} onClick=${() => updateSearchParams({ status: filter.key === 'ALL' ? null : filter.key })}>
            ${t(`whatsappCampaigns.list.filters.${filter.key}`)}
          </button>`)}
        </div>
        <label class="wa-campaign-search">
          <${Icon} name="IconSearch" size=${14} />
          <input class="text-input" type="search" value=${searchText} placeholder=${t('whatsappCampaigns.list.search')} aria-label=${t('whatsappCampaigns.list.search')}
            onInput=${(/** @type {Event} */ event) => setSearchText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
      </div>
      ${renderBody()}
    </div>
    ${pendingConfirm
      ? html`<${Modal} title=${t(pendingConfirm.action === 'cancel' ? 'whatsappCampaigns.confirm.cancelTitle' : 'whatsappCampaigns.confirm.deleteTitle', { name: pendingConfirm.campaign.name })}
          subtitle=${t(pendingConfirm.action === 'cancel' ? 'whatsappCampaigns.confirm.cancel' : 'whatsappCampaigns.confirm.delete')}
          confirmLabel=${t(pendingConfirm.action === 'cancel' ? 'whatsappCampaigns.actions.cancel' : 'whatsappCampaigns.actions.delete')} confirmAccent="danger"
          isLoading=${isConfirming} onClose=${() => setPendingConfirm(null)} onConfirm=${confirm} />`
      : null}
  <//>`;
}
